import { useState, useCallback } from 'react'
import { shuffle } from '../utils'

export type Difficulty = 'easy' | 'medium' | 'hard'

export type GamePhase = 'memorise' | 'shopping' | 'done'

export interface DifficultyConfig {
  listSize:    number  // items on the shopping list to memorise
  cartSize:    number  // how many list items are already in the cart (subset)
  distractors: number  // extra words not in the list shown as wrong options
}

// missing = listSize - cartSize (these are the correct answers)
// total options = missing + distractors
export const DIFFICULTY_CONFIG: Record<Difficulty, DifficultyConfig> = {
  easy:   { listSize: 5, cartSize: 3, distractors: 3 },  // 2 missing, pick from 5 options
  medium: { listSize: 7, cartSize: 4, distractors: 4 },  // 3 missing, pick from 7 options
  hard:   { listSize: 9, cartSize: 5, distractors: 5 },  // 4 missing, pick from 9 options
}

export interface ShoppingItem {
  id:       number
  word:     string
  imageUrl: string | null
  emoji:    string
}

export interface GameState {
  phase:        GamePhase
  shoppingList: ShoppingItem[]  // full list to memorise
  cartItems:    ShoppingItem[]  // subset already in cart (shown as images)
  missingItems: ShoppingItem[]  // items in list but NOT in cart (correct answers)
  options:      ShoppingItem[]  // missingItems + distractors, shuffled
  selected:     Set<number>     // ids the player has ticked
  moves:        number
  startTime:    number | null
  endTime:      number | null
  gameComplete: boolean
  correctCount: number
  totalNeeded:  number          // = missingItems.length
}

function buildState(difficulty: Difficulty, items: ShoppingItem[]): GameState {
  const { listSize, cartSize, distractors } = DIFFICULTY_CONFIG[difficulty]

  // We need listSize items for the list + distractors (not in list) for wrong options
  const needed = listSize + distractors
  const pool   = shuffle(items).slice(0, Math.min(needed, items.length))

  const listItems       = pool.slice(0, Math.min(listSize, pool.length))
  const distractorItems = pool.slice(listItems.length)

  // Split the list: first cartSize go into the cart, the rest are missing
  const shuffledList = shuffle(listItems)
  const cartItems    = shuffledList.slice(0, Math.min(cartSize, shuffledList.length))
  const missingItems = shuffledList.slice(cartItems.length)

  // Options = missing (correct) + distractors (wrong), shuffled
  const options = shuffle([...missingItems, ...distractorItems])

  return {
    phase:        'memorise',
    shoppingList: listItems,
    cartItems,
    missingItems,
    options,
    selected:     new Set(),
    moves:        0,
    startTime:    null,
    endTime:      null,
    gameComplete: false,
    correctCount: 0,
    totalNeeded:  missingItems.length,
  }
}

export function useShoppingList(difficulty: Difficulty, items: ShoppingItem[]) {
  const [state, setState] = useState<GameState>(() => buildState(difficulty, items))

  const resetGame = useCallback(() => {
    setState(buildState(difficulty, items))
  }, [difficulty, items])

  const startShopping = useCallback(() => {
    setState((prev) => ({
      ...prev,
      phase:     'shopping',
      startTime: Date.now(),
    }))
  }, [])

  const toggleOption = useCallback((id: number) => {
    setState((prev) => {
      if (prev.phase !== 'shopping') return prev
      const next = new Set(prev.selected)
      if (next.has(id)) {
        next.delete(id)
      } else {
        if (next.size >= prev.totalNeeded) return prev  // cap at exact count
        next.add(id)
      }
      return { ...prev, selected: next }
    })
  }, [])

  const checkAnswers = useCallback(() => {
    setState((prev) => {
      if (prev.phase !== 'shopping') return prev

      const missingIds = new Set(prev.missingItems.map((i) => i.id))
      let correct = 0
      prev.selected.forEach((id) => {
        if (missingIds.has(id)) correct++
      })

      return {
        ...prev,
        phase:        'done',
        moves:        prev.moves + 1,
        endTime:      Date.now(),
        gameComplete: true,
        correctCount: correct,
      }
    })
  }, [])

  return { state, resetGame, startShopping, toggleOption, checkAnswers }
}
